import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, message, Spin, Card, Typography, Empty, Tag } from "antd";
import { EditOutlined, FileTextOutlined } from "@ant-design/icons";
import "react-quill/dist/quill.snow.css";
import useAuth from "../hooks/useAuth";
import instance from "../api/cmsapi";
import { DpatQaMemoDocumentDisplay } from "./DpatQaMemoDisplay";
import DpatQaMemoTemplateView from "./DpatQaMemoTemplateView";
import DpatCircleProgressLoader from "./DpatCircleProgressLoader";
import { quillFormats, quillModules } from "../utils/quillEditorConfig";
import { useGetCommentsBySpecificTable, useGetScoreSheetSummary } from "../service/comment.service";
import { useDpatCommentFeedback } from "../context/DpatCommentFeedbackContext";
import {
  buildDpatQaMemoTemplate,
  mergeQaMemoDocument,
  parseQaMemoAddendum,
  QA_MEMO_TABLE_ID,
  QA_MEMO_TEMPLATE_VERSION,
  resolveDistrictDisplayName,
} from "../utils/dpatQaMemoTemplate";

const ReactQuill = lazy(() => import("react-quill"));

const DPAT_PRIMARY = "#16325a";
const AUTO_SAVE_DELAY_MS = 1500;

function formatCommentDate(date) {
  if (!date) return "—";
  if (Array.isArray(date)) return date.filter((x) => x != null).join("/");
  return String(date);
}

function QualityAssuranceEditor({ year, districtId, district, assessmentStatus }) {
  const { user } = useAuth();
  const [existingCommentId, setExistingCommentId] = useState(null);
  const [memoRecord, setMemoRecord] = useState(null);
  const [addendum, setAddendum] = useState("");
  const [editing, setEditing] = useState(false);
  const [saveState, setSaveState] = useState("idle");

  const addendumRef = useRef("");
  const seedingRef = useRef(false);
  const saveTimerRef = useRef(null);
  const lastSavedAddendumRef = useRef("");
  const lastSavedMergedRef = useRef("");

  const roleNames = useMemo(
    () => user?.user?.userRoles?.map((role) => role.name) ?? [],
    [user]
  );

  const isQualityAssurance = roleNames.includes("DPAT QUALITY ASSURANCE");
  const isSuperuser = roleNames.includes("Superuser");
  const isPetitionCommittee = roleNames.includes("DPAT PETITION COMMITTEE");
  const isTechnicalTeam = roleNames.includes("DPAT TECHNICAL TEAM");
  const isDistrictUser = roleNames.includes("DPAT DISTRICT USERS");

  const canEditMemo = isQualityAssurance || isSuperuser;
  const canViewMemo =
    canEditMemo ||
    isPetitionCommittee ||
    isTechnicalTeam ||
    isDistrictUser ||
    isSuperuser;

  const currentUsername = user?.user?.username || "";
  const currentFullName = user?.user?.fullName || "";
  const districtName = resolveDistrictDisplayName(district);

  const { scoreSheetSummary: cachedSummary } = useDpatCommentFeedback();
  const { data: scoreSheetResponse, isLoading: scoreLoading } = useGetScoreSheetSummary(
    districtId,
    year,
    "DPAT"
  );

  const summaryData = useMemo(
    () => scoreSheetResponse?.data || scoreSheetResponse || cachedSummary || null,
    [scoreSheetResponse, cachedSummary]
  );

  const { data: memoRows = [], isLoading: memoLoading } = useGetCommentsBySpecificTable(
    districtId,
    year,
    "DPAT",
    QA_MEMO_TABLE_ID,
    {
      onError: () => {
        if (canEditMemo) {
          message.error("Failed to fetch memo");
        }
      },
    },
  );

  const queryMemo = Array.isArray(memoRows) && memoRows.length > 0 ? memoRows[0] : null;
  const displayMemo = memoRecord || queryMemo;

  const templateHtml = useMemo(
    () =>
      buildDpatQaMemoTemplate({
        districtName,
        year,
        summary: summaryData,
      }),
    [districtName, year, summaryData]
  );

  const mergedHtml = useMemo(
    () => mergeQaMemoDocument(templateHtml, addendum),
    [templateHtml, addendum]
  );

  useEffect(() => {
    if (editing) return;
    const memo = Array.isArray(memoRows) && memoRows.length > 0 ? memoRows[0] : null;
    if (memo) {
      setMemoRecord(memo);
      setExistingCommentId(memo.id);
      const savedAddendum = parseQaMemoAddendum(memo);
      setAddendum(savedAddendum);
      addendumRef.current = savedAddendum;
      lastSavedAddendumRef.current = savedAddendum;
      lastSavedMergedRef.current = memo.comments || "";
      return;
    }
    if (memoLoading || existingCommentId) return;
    setMemoRecord(null);
    setAddendum("");
    addendumRef.current = "";
    lastSavedAddendumRef.current = "";
    lastSavedMergedRef.current = "";
  }, [editing, existingCommentId, memoLoading, memoRows]);

  const persistMemo = useCallback(
    async ({ addendumHtml, silent = false }) => {
      if (!currentUsername || !districtId || !year || !canEditMemo) {
        return false;
      }

      const nextAddendum = addendumHtml ?? addendumRef.current ?? "";
      const merged = mergeQaMemoDocument(templateHtml, nextAddendum);

      if (merged === lastSavedMergedRef.current && existingCommentId) {
        return true;
      }

      const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
      const payload = {
        id: existingCommentId || 0,
        username: currentUsername,
        fullName: currentFullName,
        userRole: "DQA",
        type: "DPAT",
        districtId,
        year,
        tableCommented: QA_MEMO_TABLE_ID,
        comments: merged,
        commentDate: memoRecord?.commentDate || commentDate,
        updateDate: commentDate,
        dddpDataDate: commentDate,
        dddpData: {
          indicator: QA_MEMO_TABLE_ID,
          qaMemoAddendum: nextAddendum,
          qaMemoTemplateVersion: QA_MEMO_TEMPLATE_VERSION,
        },
      };

      try {
        setSaveState("saving");
        const isUpdate = Boolean(existingCommentId);
        let savedMemo;
        if (isUpdate) {
          const response = await instance.put(`comments/${existingCommentId}`, payload);
          savedMemo = response.data;
        } else {
          const response = await instance.post("comments", payload);
          savedMemo = response.data;
          setExistingCommentId(savedMemo.id);
        }

        setMemoRecord(savedMemo);
        lastSavedAddendumRef.current = nextAddendum;
        lastSavedMergedRef.current = merged;
        setSaveState("saved");
        if (!silent) {
          message.success(isUpdate ? "Memo updated successfully" : "Memo added successfully");
        }
        return true;
      } catch (error) {
        console.error("Failed to save QA memo:", error);
        setSaveState("error");
        if (!silent) {
          message.error(`Failed to save memo: ${error.response?.data?.message || error.message}`);
        }
        return false;
      }
    },
    [
      canEditMemo,
      currentFullName,
      currentUsername,
      districtId,
      existingCommentId,
      memoRecord?.commentDate,
      templateHtml,
      year,
    ]
  );

  useEffect(() => {
    if (!canEditMemo || !assessmentStatus || scoreLoading || !summaryData) {
      return;
    }
    if (seedingRef.current) {
      return;
    }

    const shouldSeed = !existingCommentId;
    const templateChanged =
      existingCommentId &&
      mergeQaMemoDocument(templateHtml, lastSavedAddendumRef.current) !== lastSavedMergedRef.current;

    if (!shouldSeed && !templateChanged) {
      return;
    }

    seedingRef.current = true;
    persistMemo({ addendumHtml: lastSavedAddendumRef.current, silent: true }).finally(() => {
      seedingRef.current = false;
    });
  }, [
    assessmentStatus,
    canEditMemo,
    existingCommentId,
    persistMemo,
    scoreLoading,
    summaryData,
    templateHtml,
  ]);

  useEffect(() => {
    addendumRef.current = addendum;
  }, [addendum]);

  useEffect(() => {
    if (!canEditMemo || !assessmentStatus || !existingCommentId) {
      return;
    }
    if (addendum === lastSavedAddendumRef.current) {
      return;
    }

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => {
      persistMemo({ addendumHtml: addendum, silent: true });
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [addendum, assessmentStatus, canEditMemo, existingCommentId, persistMemo]);

  const handleAddendumChange = (value) => {
    setAddendum(value);
    setSaveState("pending");
  };

  const handleManualSave = async () => {
    const ok = await persistMemo({ addendumHtml: addendum, silent: false });
    if (ok) {
      setEditing(false);
    }
  };

  const showMemoCard = Boolean(displayMemo || (canEditMemo && assessmentStatus && summaryData));
  const resolvedCommentId = existingCommentId || queryMemo?.id;
  const showEditor = canEditMemo && assessmentStatus && (editing || !resolvedCommentId);
  const waitingForMemo = memoLoading;

  if (!canViewMemo) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-4">
      {waitingForMemo ? (
        <DpatCircleProgressLoader
          done={!waitingForMemo}
          label="Loading memo"
        />
      ) : (
        <>
          {showEditor && (
            <div className="mb-6">
              <div className="mb-3 flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
                <Tag color="processing">QA memo template</Tag>
                {saveState === "saving" && <Tag color="default">Saving…</Tag>}
                {saveState === "saved" && <Tag color="success">Saved</Tag>}
                {saveState === "error" && <Tag color="error">Save failed</Tag>}
              </div>

              <Card bordered className="mb-4 shadow-sm dpat-qa-memo-card" styles={{ body: { padding: "20px 24px" } }}>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12, textAlign: "center", width: "100%" }}>
                  Template (auto-populated — cannot be edited)
                </Typography.Text>
                <DpatQaMemoTemplateView
                  districtName={districtName}
                  year={year}
                  summary={summaryData}
                />
              </Card>

              <div className="overflow-hidden rounded-lg border border-[#e6ebf1] bg-white">
                <div className="border-b border-[#e6ebf1] px-4 py-2">
                  <Typography.Text strong>Additional notes</Typography.Text>
                  <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                    Add bonus reasons or other QA notes here. The template above stays locked.
                  </Typography.Text>
                </div>
                <Suspense fallback={<div className="flex justify-center py-8"><Spin /></div>}>
                  <ReactQuill
                    value={addendum}
                    onChange={handleAddendumChange}
                    modules={quillModules}
                    formats={quillFormats}
                    className="petition-quill-editor"
                    style={{ height: "220px", marginBottom: "50px", width: "100%" }}
                  />
                </Suspense>
              </div>

              <div className="mt-4 flex justify-center gap-3">
                {existingCommentId && (
                  <Button onClick={() => setEditing(false)} disabled={saveState === "saving"}>
                    Done
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={handleManualSave}
                  loading={saveState === "saving"}
                  style={{
                    backgroundColor: DPAT_PRIMARY,
                    borderColor: DPAT_PRIMARY,
                    minWidth: 180,
                  }}
                >
                  Save Memo
                </Button>
              </div>
            </div>
          )}

          {showMemoCard && !showEditor ? (
            <Card bordered className="shadow-sm dpat-qa-memo-card" styles={{ body: { padding: "20px 24px" } }}>
              <div className="mb-4 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
                <Tag color="blue" icon={<FileTextOutlined />}>
                  DPAT QUALITY ASSURANCE — QA Memo
                </Tag>
                {canEditMemo && assessmentStatus && (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </Button>
                )}
              </div>
              <DpatQaMemoDocumentDisplay
                districtName={districtName}
                year={year}
                summary={summaryData}
                addendumHtml={addendum || (queryMemo ? parseQaMemoAddendum(queryMemo) : "")}
              />
              <Typography.Text
                type="secondary"
                style={{ display: "block", marginTop: 12, textAlign: "right", fontSize: 12 }}
              >
                {formatCommentDate(displayMemo?.commentDate)}
              </Typography.Text>
            </Card>
          ) : (
            !canEditMemo && (
              <Card bordered className="text-center" style={{ background: "#f8fafc" }}>
                <Empty description="No QA memo has been published for this district yet." />
              </Card>
            )
          )}
        </>
      )}
    </div>
  );
}

export default QualityAssuranceEditor;
